"use client";

import { AppBar, CircularProgress, Tab } from "@mui/material";
import Grid from "@mui/material/Grid2";
import React from "react";
import { useParams } from "react-router-dom";

import Tabs from "@/components/atoms/ZoomAwareTabs";
import PageContainer from "@/components/atoms/PageContainer";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useGetOrderById from "@/hooks/requests/orders/use-get-order-by-id";
import useTabs from "@/hooks/use-tabs";
import { OrderDetailsMessages } from "@/messages/order-details/order-details.messages";

import ChatTab from "./components/ChatTab";
import OrderPdfPrint from "./components/OrderPdfPrint";
import OrderDetailsTab from "./components/orderDetailsTab";
import { TABS_ITEMS } from "./constants";
import { OrderDetailsTabs } from "./types";

type Props = {
  messages: OrderDetailsMessages;
  isPrint?: boolean;
};

function OrderDetailPage({ messages, isPrint = false }: Props) {
  const { lang } = useLanguage();
  const { orderId } = useParams<{ orderId: string }>();
  const _id = Number(orderId) || 0;

  const { data: order, isLoading: isLoadingOrder, error } = useGetOrderById({
    _id: isPrint ? 0 : _id,
  });
  const conversationId = order?.conversationId;

  const TABS = TABS_ITEMS(messages);
  const initialTab = "orderDetailsTab";
  const tabs = TABS;

  const { activeTab, changeTab, tabsOrientation } = useTabs<OrderDetailsTabs>({ initialTab, tabs });

  const isDetailsTab = activeTab === "orderDetailsTab";
  const isChatTab = activeTab === "chatTab";

  const BREAD_CRUMBS = [{ title: messages.orderBreadCrumbTitle, href: ROUTE_URLS.orders(lang) }];

  return (
    <PageLayout
      title={isPrint ? messages.orderPrintTitle : messages.orderDetailsTitle}
      breadCrumbs={BREAD_CRUMBS}
      messages={messages}
    >
      <PageContainer>
        {isPrint ? (
          <OrderPdfPrint messages={messages} orderId={_id} />
        ) : (
          <Suspense isSuspended={isLoadingOrder} fallback={<CircularProgress />}>
            <>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 12, lg: 3 }}>
                  <AppBar position="static">
                    <Tabs
                      orientation={tabsOrientation}
                      value={activeTab}
                      onChange={(_, newTab) => changeTab(newTab)}
                      sx={{ width: "100%" }}
                    >
                      {TABS.map((tab, index) => (
                        <Tab key={index} label={tab.label} value={tab.value} />
                      ))}
                    </Tabs>
                  </AppBar>
                </Grid>
              </Grid>

              {isDetailsTab && (
                <OrderDetailsTab messages={messages} order={order} error={error} isPrint={false} />
              )}
              {isChatTab && conversationId && <ChatTab conversationId={conversationId} />}
            </>
          </Suspense>
        )}
      </PageContainer>
    </PageLayout>
  );
}

export default OrderDetailPage;
